import { getAccountInfo, isLoggedIn, setAccountInfo } from '@/plugins/cdc/shared.js';


/**
 * This CDC plugin helps to handle subscriptions.
 * We only have very little documentation about how everything works but this is where you could have a look at:
 *
 * @here
 * https://collaboration.ivoclarvivadent.com/pages/viewpage.action?spaceKey=CDC&title=gigyaConsent+snippet
 * https://collaboration.ivoclarvivadent.com/download/attachments/129931906/snippet.js.txt?version=2&modificationDate=1630920637514&api=v2
 */

const subscriptionNameMap = {
  globalMarketingPolicy: 'globalMarketingPolicy.email.isSubscribed',
};

const subscriptionTextMap = {
  globalMarketingPolicy: 'globalMarketingMsg',
};

/**
 * Saves a subscription.
 *
 * @param {String} subscriptionName - The name of the subscription.
 * @param {String} locale - The users locale.
 * @param {Boolean} value - If the subscription should be active or not.
 *
 * @returns {Promise}
 */
export function saveSubscription(subscriptionName, locale, value = true) {
  const tags = [`appSource:${window.gigya?.thisScript?.globalConf?.regSource}`, `locale:${getFormattedLocale(locale)}`];
  const subscriptions = setValueInObjectByKey(subscriptionName, {
    email: {
      isSubscribed: value,
      tags,
    },
  });

  return new Promise((resolve, reject) => {
    if (!isLoggedIn) {
      // eslint-disable-next-line no-console
      console.error('CDC Consent - Subscription save failed: not logged in');
      reject(new Error('User not logged in'));

      return;
    }

    if (window.environment === 'styleguide') {
      resolve(true); // Change this value to test the different states (true, false)

      return;
    }

    setAccountInfo({ subscriptions }).then(
      (response) => {
        resolve(response);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('CDC Consent - Subscription save failed:', error);
        reject(error);
      },
    );
  });
}

/**
 * Returns a subscription.
 *
 * @param {String} subscriptionName - The name of the subscription.
 * @param {String} locale - The users locale.
 *
 * @returns {Promise}
 */
export function getSubscription(subscriptionName, locale) {
  return new Promise((resolve, reject) => {
    if (!isLoggedIn) {
      // eslint-disable-next-line no-console
      console.error('CDC Consent - Subscription fetch failed: not logged in');
      reject(new Error('User not logged in'));

      return;
    }

    if (window.environment === 'styleguide') {
      resolve({
        value: false, // Change this value to test the different states (true, false)
      });

      return;
    }

    const promise2 = new window.gigya.Promise((event) => window.gigya.accounts.getSchema({ callback: event }));
    const accountInfo = getAccountInfo({ include: 'all' });

    Promise.all([accountInfo, promise2])
      .then((data) => {
        const userAccount = data[0];
        const textData = data[1];

        resolve({
          value: getSubscriptionValue(userAccount.subscriptions, subscriptionName),
          text: getGenericConsentText(
            textData.preferencesSchema,
            getFormattedLocale(locale),
            subscriptionTextMap[subscriptionName],
          ),
        });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('CDC Consent - Subscription fetch failed:', error);
        reject(error);
      });
  });
}

/**
 * Returns a text from the gigya schema.
 *
 * @param {Object} prefSchema - A key value mapping of text objects.
 * @param {String} locale - The users locale.
 * @param {String} consentName - The consent name.
 *
 * @returns {String}
 */
function getGenericConsentText(prefSchema, locale, consentName) {
  try {
    return window.gigya.thisScript.globalConf.getConsentText(prefSchema, locale, consentName, 0);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('CDC Consent - Could not load correct message by key:', error);

    return '';
  }
}

/**
 * Returns a subscription.
 *
 * @param {Object} subscriptions - A key value mapping of subscription objects.
 * @param {String} subscriptionName - The subscription name.
 *
 * @returns {Boolean}
 */
function getSubscriptionValue(subscriptions, subscriptionName) {
  if (subscriptionName in subscriptions) {
    return getValueInObjectByKey(subscriptions, subscriptionNameMap[subscriptionName]);
  }

  return false;
}

/**
 * Finds a key within an object.
 *
 * eg: 'foo.bar.name' will return 'found' for this object '{ foo: { bar: { name: 'found' } } }'
 *
 * @param {Object} object - Some javascript object.
 * @param {String} propertyPath - A dot seperated path to some property.
 *
 * @returns {String}
 */
function getValueInObjectByKey(object, propertyPath) {
  return propertyPath.split('.').reduce((item, key) => item[key], object);
}

/**
 * Sets the value of a property within an object.
 *
 * eg: 'foo.bar.name' will create a new object with the given object '{ test: { isActive: true } }'
 * '{ foo: { bar: { name: { test: { isActive: true } } } } }'
 *
 * @param {String} propertyPath - A dot seperated path to some property.
 * @param {Object} object - Some javascript object.
 *
 * @returns {Object}
 */
function setValueInObjectByKey(propertyPath, object) {
  return propertyPath
    .split('.')
    .reverse()
    .reduce(
      (item, key) => ({
        [key]: item,
      }),
      object,
    );
}

/**
 * Returns a formatted locale for CDC.
 *
 * @param {String} locale - Some locale as string.
 *
 * @returns {String}
 */
function getFormattedLocale(locale) {
  return locale.replace('_', '-');
}

/**
 * Define the plugin structure.
 */
export const cdcConsentPlugin = {
  saveSubscription,
  getSubscription,
};
